import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";

const CartContext = createContext(null);

const STORAGE_KEY = "faith_athletic_cart_v2";
const EMAIL_KEY = "faith_athletic_customer_email";

const lineKey = (it) => `${it.product_id}|${it.size || ""}|${it.color || ""}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [email, setEmailState] = useState("");
  const trackTimer = useRef(null);
  const mounted = useRef(false);

  // Hydrate from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
      const e = localStorage.getItem(EMAIL_KEY);
      if (e) setEmailState(e);
    } catch (e) {
      console.error(e);
    }
  }, []);

  // Persist
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (email) localStorage.setItem(EMAIL_KEY, email);
  }, [email]);

  // Debounced abandoned cart tracking
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    if (!email) return;
    if (trackTimer.current) clearTimeout(trackTimer.current);
    trackTimer.current = setTimeout(async () => {
      try {
        await api.post("/cart/track", {
          email,
          items: items.map((i) => ({
            product_id: i.product_id,
            quantity: i.quantity,
            size: i.size || null,
            color: i.color || null,
          })),
          origin_url: window.location.origin,
        });
      } catch (e) {
        // silent
      }
    }, 2000);
    return () => trackTimer.current && clearTimeout(trackTimer.current);
  }, [items, email]);

  const setEmail = (v) => setEmailState(v || "");

  const add = (product, { quantity = 1, size = null, color = null } = {}) => {
    setItems((prev) => {
      const line = {
        product_id: product.id,
        name: product.name,
        price: product.price,
        image_url: product.image_url,
        size,
        color,
        quantity,
      };
      const key = lineKey(line);
      const existing = prev.find((i) => lineKey(i) === key);
      if (existing) {
        return prev.map((i) =>
          lineKey(i) === key ? { ...i, quantity: i.quantity + quantity } : i
        );
      }
      return [...prev, line];
    });
    setOpen(true);
  };

  const remove = (key) =>
    setItems((prev) => prev.filter((i) => lineKey(i) !== key));

  const updateQty = (key, quantity) =>
    setItems((prev) =>
      prev
        .map((i) => (lineKey(i) === key ? { ...i, quantity } : i))
        .filter((i) => i.quantity > 0)
    );

  const clear = () => setItems([]);

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.price * i.quantity, 0),
    [items]
  );
  const itemCount = useMemo(
    () => items.reduce((s, i) => s + i.quantity, 0),
    [items]
  );

  return (
    <CartContext.Provider
      value={{
        items,
        add,
        remove,
        updateQty,
        clear,
        subtotal,
        itemCount,
        open,
        setOpen,
        email,
        setEmail,
        lineKey,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
